import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BellRinging, ListChecks, Clock, ArrowRight, Timer } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const SNOOZE_KEY = "sp.checklistSnooze";
const SNOOZE_MS = 10 * 60 * 1000; // re-remind 10 minutes later if still not done
const POLL_MS = 60 * 1000;

function loadSnooze() { try { return JSON.parse(localStorage.getItem(SNOOZE_KEY) || "{}"); } catch { return {}; } }
function saveSnooze(m) { try { localStorage.setItem(SNOOZE_KEY, JSON.stringify(m)); } catch { /* ignore */ } }

function fmt12(t) {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return t;
  const ap = h < 12 ? "AM" : "PM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
}

// MyGate-style in-app reminder: a blocking dialog that fires when a checklist
// task is still open 10 min past its time, with Open / Remind-in-10 actions.
export default function ChecklistDueAlert() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  // Security does most of the checklist, so they get the overdue reminder too.
  const isStaff = ["MANAGER", "CHAIRPERSON", "ADMIN", "SECURITY"].includes(user?.role);

  const [due, setDue] = useState([]);
  const [snooze, setSnooze] = useState(loadSnooze);
  const [, force] = useState(0); // ticks so a snoozed item reappears after its window

  useEffect(() => {
    if (!isStaff) return undefined;
    let alive = true;
    async function poll() {
      try { const { data } = await api.get("/api/checklist/due"); if (alive) setDue(data.items || []); } catch { /* offline / not staff */ }
    }
    poll();
    const id = setInterval(poll, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [isStaff]);

  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  if (!isStaff) return null;
  if (loc.pathname === "/checklist") return null; // they're already on the checklist

  const nowMs = Date.now();
  const active = due.find((it) => !(snooze[it.id] && snooze[it.id] > nowMs));
  const showing = !!active;

  function snoozeActive() {
    if (!active) return;
    const m = { ...snooze, [active.id]: Date.now() + SNOOZE_MS };
    setSnooze(m); saveSnooze(m);
  }
  function openChecklist() { snoozeActive(); navigate("/checklist"); }

  return (
    <AnimatePresence>
      {showing && (
        <motion.div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-5 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="checklist-due-alert">
          <motion.div initial={{ scale: 0.9, y: 16, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            className="w-full max-w-sm overflow-hidden rounded-3xl border border-neon-amber/30 bg-ink-900" style={{ boxShadow: "0 0 40px rgba(255,184,0,0.25)" }}>
            <div className="flex flex-col items-center gap-3 bg-gradient-to-b from-neon-amber/15 to-transparent px-6 pt-7 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-neon-amber/20 text-neon-amber">
                <BellRinging size={30} weight="fill" />
              </div>
              <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-neon-amber">Checklist reminder</p>
            </div>

            <div className="px-6 pb-2 pt-2 text-center">
              <p className="font-display text-lg font-bold leading-snug text-white">{active.task}</p>
              <div className="mt-3 inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-2">
                <span className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white">
                  <Clock size={15} weight="bold" className="text-neon-cyan" /> {fmt12(active.time)}
                </span>
                <span className="h-4 w-px bg-white/15" />
                <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-neon-amber">
                  <Timer size={14} weight="bold" /> {active.overdueMin} min overdue
                </span>
              </div>
              <p className="mt-3 text-[12px] text-white/55">This task is still open past its scheduled time. Please complete it or get a reminder shortly.</p>
            </div>

            <div className="flex flex-col gap-2 p-5">
              <button onClick={openChecklist} data-testid="alert-open-checklist"
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-neon-cyan px-4 py-3.5 text-[14px] font-bold text-black shadow-glow-cyan">
                <ListChecks size={18} weight="fill" /> Open checklist <ArrowRight size={16} weight="bold" />
              </button>
              <button onClick={snoozeActive} data-testid="alert-snooze"
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-[13px] font-bold text-white/80 hover:bg-white/10">
                <Timer size={16} weight="bold" /> Remind me in 10 min
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
